// Chemin: C:\gestion-copro\src\pages\auth\GmailCallbackPage.tsx
// Page callback OAuth Gmail - Copro Protect

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleGmailCallback } from '../../services/gmail.service';

type CallbackStatus = 'processing' | 'success' | 'error';

const GmailCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const processCallback = async () => {
      // Récupérer le code d'autorisation de l'URL
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      
      // Récupérer l'ID de la copropriété stocké avant la redirection
      const coproprieteId = sessionStorage.getItem('gmail_auth_copropriete_id');

      // Vérifier les erreurs Google
      if (error) {
        setStatus('error');
        setErrorMessage(
          error === 'access_denied'
            ? "Vous avez refusé l'accès à Gmail."
            : `Erreur Google: ${error}`
        );
        return;
      }

      // Vérifier que le code est présent
      if (!code) {
        setStatus('error');
        setErrorMessage("Code d'autorisation manquant.");
        return;
      }

      // Vérifier que l'ID de copropriété est présent
      if (!coproprieteId) {
        setStatus('error');
        setErrorMessage(
          "Session expirée. Veuillez relancer la connexion Gmail depuis l'application."
        );
        return;
      }

      try {
        // Échanger le code contre des tokens
        const result = await handleGmailCallback(code, coproprieteId);

        if (result.success && result.email) {
          setStatus('success');
          setEmail(result.email);
          
          // Nettoyer le sessionStorage
          sessionStorage.removeItem('gmail_auth_copropriete_id');
          
          // Rediriger vers la messagerie après 2 secondes
          setTimeout(() => {
            navigate('/admin/messagerie');
          }, 2000);
        } else {
          setStatus('error');
          setErrorMessage(result.error || 'Erreur inconnue lors de la connexion.');
        }
      } catch (err) {
        console.error('Erreur callback Gmail:', err);
        setStatus('error');
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'Erreur lors de la connexion à Gmail.'
        );
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Logo ou icône */}
        <div className="mb-6">
          <div
            className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              status === 'processing'
                ? 'bg-blue-100'
                : status === 'success'
                ? 'bg-green-100'
                : 'bg-red-100'
            }`}
          >
            {status === 'processing' && (
              <svg
                className="w-8 h-8 text-blue-600 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {status === 'success' && (
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
            {status === 'error' && (
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Titre */}
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          {status === 'processing' && 'Connexion en cours...'}
          {status === 'success' && 'Gmail connecté !'}
          {status === 'error' && 'Erreur de connexion'}
        </h1>

        {/* Message */}
        <p className="text-gray-600 mb-6">
          {status === 'processing' &&
            'Veuillez patienter pendant que nous finalisons la connexion à Gmail.'}
          {status === 'success' && (
            <>
              Le compte <span className="font-medium text-gray-900">{email}</span> a
              été connecté avec succès. Redirection en cours...
            </>
          )}
          {status === 'error' && (
            <span className="text-red-600">{errorMessage}</span>
          )}
        </p>

        {/* Boutons */}
        {status === 'error' && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/messagerie')}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour à la messagerie
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Retour au tableau de bord
            </button>
          </div>
        )}

        {status === 'success' && (
          <button
            onClick={() => navigate('/admin/messagerie')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Accéder à la messagerie
          </button>
        )}
      </div>
    </div>
  );
};

export default GmailCallbackPage;